/**
 * Projects workspace — the Clients → Projects → Tasks hierarchy.
 *
 * Left rail lists Clients (Accounts). Selecting a client shows its
 * Projects as cards with a live completion % (derived from the project's
 * tasks, never stored). Selecting a project opens a detail pane with
 * editable metadata, links, tags, and the project's task list grouped by
 * status. Human CRUD writes directly — this is an internal tool, no
 * approvals gating (agents still propose elsewhere).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Archive,
  ArchiveRestore,
  ArrowUpDown,
  Calendar,
  ChevronRight,
  Columns3,
  FolderKanban,
  LayoutGrid,
  Link2,
  MessageSquare,
  Paperclip,
  Plus,
  Rows3,
  Search,
  Send,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { MentionTextarea } from '../components/MentionTextarea';
import { FileAttachButton } from '../components/FileAttachButton';
import { EmptyState, LoadingState } from '../components/States';
import { queueMentionEmails } from '../lib/crm/notifyMentions';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import { subscribeAccounts } from '../lib/db/accounts';
import { subscribeTasks } from '../lib/db/tasks';
import {
  PROJECT_STATUSES,
  addProjectAttachment,
  addProjectComment,
  addProjectLink,
  bootstrapProjectsIfEmpty,
  createProject,
  removeProjectAttachment,
  removeProjectLink,
  subscribeProjects,
  updateProject,
} from '../lib/db/projects';
import { bootstrapAccountsIfEmpty } from '../lib/db/accounts';
import { TASK_ASSIGNEES, bootstrapTasksIfEmpty, createTask } from '../lib/db/tasks';
import { TaskDetail } from './TasksBoardView';
import { parseMentions } from '../lib/crm/mentions';
import type {
  Account,
  Project,
  ProjectStatus,
  ProjectTask,
  TaskPriority,
} from '../lib/db/types';

const PRIORITIES: readonly TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

type ProjectSort = 'updated' | 'title' | 'due' | 'completion' | 'priority';
type ProjectView = 'grid' | 'list' | 'status';

const SORT_LABEL: Record<ProjectSort, string> = {
  updated: 'Recently updated',
  title: 'Title (A–Z)',
  due: 'Due date',
  completion: 'Completion',
  priority: 'Priority',
};

const PRIORITY_RANK: Record<TaskPriority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

const STATUS_COLOR: Record<ProjectStatus, string> = {
  active: C.green,
  'on-hold': C.orange,
  completed: C.blueLight,
  archived: C.textDim,
};

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  low: C.textDim,
  medium: C.blueLight,
  high: C.orange,
  urgent: C.orangeWarm,
};

/** Completion % for a project = done tasks / total tasks (0 if none). */
function completionOf(projectId: string, tasks: ProjectTask[]): { pct: number; done: number; total: number } {
  const mine = tasks.filter((t) => t.projectId === projectId);
  const done = mine.filter((t) => t.status === 'done').length;
  const total = mine.length;
  return { pct: total > 0 ? Math.round((done / total) * 100) : 0, done, total };
}

function StatusPill({ status }: { status: ProjectStatus }) {
  const color = STATUS_COLOR[status];
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        padding: '2px 8px',
        borderRadius: 999,
        color,
        background: `${color}22`,
        border: `1px solid ${color}55`,
      }}
    >
      {status}
    </span>
  );
}

function CompletionBar({ pct }: { pct: number }) {
  return (
    <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          borderRadius: 999,
          background: `linear-gradient(90deg, ${C.magentaDeep}, ${C.magenta})`,
          transition: 'width 200ms ease',
        }}
      />
    </div>
  );
}

const fieldStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 8,
  color: C.text,
  padding: '6px 8px',
  fontSize: 13,
  fontFamily: 'inherit',
};

export function ProjectsWorkspaceView() {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  const actor = user?.displayName?.split(' ')[0] ?? 'You';

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [loaded, setLoaded] = useState(false);

  // 'ALL' scope shows every client's projects; otherwise an Account id.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');
  const [sortKey, setSortKey] = useState<ProjectSort>('updated');
  const [viewMode, setViewMode] = useState<ProjectView>('grid');

  useEffect(() => {
    let done = 0;
    const mark = () => {
      done += 1;
      if (done >= 3) setLoaded(true);
    };
    void bootstrapAccountsIfEmpty().catch(() => undefined);
    void bootstrapTasksIfEmpty().catch(() => undefined);
    void bootstrapProjectsIfEmpty().catch(() => undefined);
    const unsubA = subscribeAccounts((r) => {
      setAccounts(r);
      mark();
    });
    const unsubP = subscribeProjects((r) => {
      setProjects(r);
      mark();
    });
    const unsubT = subscribeTasks((r) => {
      // Archived tasks stay in the store but are excluded from project
      // task lists, completion %, and counts.
      setTasks(r.filter((t) => !t.archived));
      mark();
    });
    return () => {
      unsubA();
      unsubP();
      unsubT();
    };
  }, []);

  // This is the Projects tab: a flat list of every project. Client browsing
  // lives on the sibling Clients tab; the toolbar below narrows this list.
  const scopedProjects = projects;

  // Client filter → clients that actually have projects, for the dropdown.
  const clientOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const p of projects) if (!byId.has(p.clientId)) byId.set(p.clientId, p.clientName);
    return [...byId.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  // Client + status + search filter + sort → the list every view renders.
  const visibleProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = scopedProjects.filter((p) => {
      if (clientFilter !== 'all' && p.clientId !== clientFilter) return false;
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        p.clientName.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'due':
          return (a.dueDate ?? '9999-99-99').localeCompare(b.dueDate ?? '9999-99-99');
        case 'completion':
          return completionOf(b.id, tasks).pct - completionOf(a.id, tasks).pct;
        case 'priority':
          return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
        case 'updated':
        default:
          return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
      }
    });
  }, [scopedProjects, search, clientFilter, statusFilter, sortKey, tasks]);

  const selectedProject = projects.find((p) => p.id === selectedProjectId) ?? null;
  // In All-scope, resolve the selected project's client for the breadcrumb.
  const breadcrumbClientName = selectedProject?.clientName ?? null;

  if (!loaded) {
    return (
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <LoadingState rows={4} label="Loading projects" />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 16, overflow: 'hidden' }}>
      {/* Main area */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.textMuted, fontSize: 13, flex: '0 0 auto' }}>
          <FolderKanban size={14} />
          <button
            type="button"
            onClick={() => setSelectedProjectId(null)}
            style={{ background: 'none', border: 'none', color: selectedProject ? C.textMuted : '#fff', cursor: 'pointer', padding: 0, fontSize: 13 }}
          >
            Projects
          </button>
          {selectedProject && (
            <>
              {breadcrumbClientName && (
                <>
                  <ChevronRight size={13} style={{ color: C.textDim }} />
                  <button
                    type="button"
                    onClick={() => setSelectedProjectId(null)}
                    style={{ background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 0, fontSize: 13 }}
                  >
                    {breadcrumbClientName}
                  </button>
                </>
              )}
              <ChevronRight size={13} style={{ color: C.textDim }} />
              <span style={{ color: '#fff' }}>{selectedProject.title}</span>
            </>
          )}
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {selectedProject ? (
            <ProjectDetail
              project={selectedProject}
              tasks={tasks}
              projects={projects}
              canMutate={canMutate}
              actor={actor}
            />
          ) : (
            <>
              {/* Toolbar: search + status filter + sort + view toggle + new */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: '0 0 auto' }}>
                {canMutate && (
                  <button
                    type="button"
                    onClick={() => setShowNewProject((v) => !v)}
                    className="glass-btn glass-btn--primary"
                  >
                    <Plus size={13} /> New project
                  </button>
                )}
                <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
                  <Search size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: C.textDim }} />
                  <input
                    style={{ ...fieldStyle, paddingLeft: 26, width: '100%' }}
                    placeholder="Search title, client, tag…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select style={fieldStyle} value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} title="Filter by client">
                  <option value="all" style={{ background: C.ink800 }}>All clients</option>
                  {clientOptions.map((c) => (
                    <option key={c.id} value={c.id} style={{ background: C.ink800 }}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select style={fieldStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | 'all')}>
                  <option value="all" style={{ background: C.ink800 }}>All statuses</option>
                  {PROJECT_STATUSES.map((s) => (
                    <option key={s} value={s} style={{ background: C.ink800 }}>
                      {s}
                    </option>
                  ))}
                </select>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: C.textDim }}>
                  <ArrowUpDown size={13} />
                  <select style={fieldStyle} value={sortKey} onChange={(e) => setSortKey(e.target.value as ProjectSort)}>
                    {(Object.keys(SORT_LABEL) as ProjectSort[]).map((k) => (
                      <option key={k} value={k} style={{ background: C.ink800 }}>
                        {SORT_LABEL[k]}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'inline-flex', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, overflow: 'hidden' }}>
                  {([['grid', LayoutGrid], ['list', Rows3], ['status', Columns3]] as const).map(([v, Icon]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setViewMode(v)}
                      title={v === 'status' ? 'Group by status' : v === 'list' ? 'List' : 'Grid'}
                      className="glass-btn"
                      style={{
                        padding: '6px 9px',
                        borderRadius: 0,
                        border: 'none',
                        background: viewMode === v ? 'rgba(255,0,255,0.18)' : 'transparent',
                        color: viewMode === v ? '#fff' : C.textMuted,
                      }}
                    >
                      <Icon size={14} />
                    </button>
                  ))}
                </div>
              </div>

              {showNewProject && canMutate && (
                <NewProjectForm client={null} accounts={accounts} actor={actor} onDone={() => setShowNewProject(false)} />
              )}

              <div style={{ fontSize: 11, color: C.textDim, flex: '0 0 auto' }}>
                {visibleProjects.length} project{visibleProjects.length === 1 ? '' : 's'}
              </div>

              <ProjectsCollection
                projects={visibleProjects}
                tasks={tasks}
                view={viewMode}
                showClient
                onOpen={(id) => setSelectedProjectId(id)}
                emptyHint={
                  search || statusFilter !== 'all' || clientFilter !== 'all'
                    ? 'No projects match your filters.'
                    : 'No projects yet — create one with New project.'
                }
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Projects collection — grid / list / grouped-by-status views
// ---------------------------------------------------------------------------

function ProjectCard({ p, tasks, showClient, onOpen }: { p: Project; tasks: ProjectTask[]; showClient: boolean; onOpen: (id: string) => void }) {
  const { pct, done, total } = completionOf(p.id, tasks);
  return (
    <GlassPanel
      variant="default"
      animate={false}
      onClick={() => onOpen(p.id)}
      style={{ padding: 14, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: C.text }}>{p.title}</div>
          {showClient && <div style={{ fontSize: 11, color: C.textDim, marginTop: 1 }}>{p.clientName}</div>}
        </div>
        <StatusPill status={p.status} />
      </div>
      {p.description && (
        <span style={{ fontSize: 12, color: C.textMuted, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {p.description}
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: C.textDim }}>
        <span style={{ color: PRIORITY_COLOR[p.priority], fontWeight: 600 }}>{p.priority}</span>
        {p.dueDate && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <Calendar size={11} /> {p.dueDate}
          </span>
        )}
        <span>· {total} task{total === 1 ? '' : 's'}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <CompletionBar pct={pct} />
        </div>
        <span style={{ fontSize: 11, color: C.textMuted, flex: '0 0 auto' }}>{pct}% · {done}/{total}</span>
      </div>
      {p.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {p.tags.map((t) => (
            <span key={t} style={{ fontSize: 10, color: C.magentaSoft, background: 'rgba(255,0,255,0.10)', padding: '1px 6px', borderRadius: 999 }}>
              {t}
            </span>
          ))}
        </div>
      )}
    </GlassPanel>
  );
}

function ProjectRow({ p, tasks, showClient, onOpen }: { p: Project; tasks: ProjectTask[]; showClient: boolean; onOpen: (id: string) => void }) {
  const { pct, done, total } = completionOf(p.id, tasks);
  return (
    <div
      onClick={() => onOpen(p.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '9px 12px',
        borderRadius: 10,
        cursor: 'pointer',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div style={{ flex: '2 1 200px', minWidth: 0 }}>
        <span style={{ fontWeight: 600, color: C.text }}>{p.title}</span>
        {showClient && <span style={{ fontSize: 11, color: C.textDim, marginLeft: 8 }}>{p.clientName}</span>}
      </div>
      <div style={{ flex: '0 0 auto' }}>
        <StatusPill status={p.status} />
      </div>
      <span style={{ flex: '0 0 56px', fontSize: 11, color: PRIORITY_COLOR[p.priority], fontWeight: 600 }}>{p.priority}</span>
      <span style={{ flex: '0 0 84px', fontSize: 11, color: C.textDim }}>{p.dueDate ?? '—'}</span>
      <div style={{ flex: '1 1 120px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 100 }}>
        <div style={{ flex: 1 }}>
          <CompletionBar pct={pct} />
        </div>
        <span style={{ flex: '0 0 auto', fontSize: 11, color: C.textMuted }}>{pct}% · {done}/{total}</span>
      </div>
    </div>
  );
}

function ProjectsCollection({
  projects,
  tasks,
  view,
  showClient,
  onOpen,
  emptyHint,
}: {
  projects: Project[];
  tasks: ProjectTask[];
  view: ProjectView;
  showClient: boolean;
  onOpen: (id: string) => void;
  emptyHint: string;
}) {
  if (projects.length === 0) {
    return <EmptyState title="No projects" hint={emptyHint} icon={<FolderKanban size={22} />} />;
  }

  if (view === 'list') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {projects.map((p) => (
          <ProjectRow key={p.id} p={p} tasks={tasks} showClient={showClient} onOpen={onOpen} />
        ))}
      </div>
    );
  }

  if (view === 'status') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {PROJECT_STATUSES.map((status) => {
          const group = projects.filter((p) => p.status === status);
          if (group.length === 0) return null;
          return (
            <div key={status} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StatusPill status={status} />
                <span style={{ fontSize: 11, color: C.textDim }}>{group.length}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {group.map((p) => (
                  <ProjectCard key={p.id} p={p} tasks={tasks} showClient={showClient} onOpen={onOpen} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
      {projects.map((p) => (
        <ProjectCard key={p.id} p={p} tasks={tasks} showClient={showClient} onOpen={onOpen} />
      ))}
    </div>
  );
}

function NewProjectForm({ client, accounts, actor, onDone }: { client: Account | null; accounts: Account[]; actor: string; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [dueDate, setDueDate] = useState('');
  const [clientId, setClientId] = useState(client?.id ?? '');
  const [saving, setSaving] = useState(false);

  const effectiveClient = client ?? accounts.find((a) => a.id === clientId) ?? null;

  const submit = async () => {
    if (!title.trim() || !effectiveClient || saving) return;
    setSaving(true);
    try {
      await createProject({
        clientId: effectiveClient.id,
        clientName: effectiveClient.name,
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        dueDate: dueDate || null,
        createdBy: actor,
      });
      onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlassPanel variant="subtle" animate={false} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: C.textMuted }}>
        {client ? `New project for ${client.name}` : 'New project'}
      </span>
      {!client && (
        <label style={{ fontSize: 12, color: C.textMuted, display: 'flex', flexDirection: 'column', gap: 4 }}>
          Client
          <select style={fieldStyle} value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="" style={{ background: C.ink800 }}>Select a client…</option>
            {[...accounts].sort((a, b) => a.name.localeCompare(b.name)).map((a) => (
              <option key={a.id} value={a.id} style={{ background: C.ink800 }}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <input style={fieldStyle} placeholder="Project title" value={title} autoFocus onChange={(e) => setTitle(e.target.value)} />
      <textarea style={{ ...fieldStyle, minHeight: 60, resize: 'vertical' }} placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: C.textMuted, display: 'flex', flexDirection: 'column', gap: 4 }}>
          Priority
          <select style={fieldStyle} value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p} style={{ background: C.ink800 }}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12, color: C.textMuted, display: 'flex', flexDirection: 'column', gap: 4 }}>
          Due date
          <input type="date" style={fieldStyle} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={submit} disabled={!title.trim() || !effectiveClient || saving} className="glass-btn" style={{ padding: '6px 12px', border: '1px solid rgba(255,0,255,0.40)', color: '#fff', opacity: !title.trim() || !effectiveClient || saving ? 0.5 : 1 }}>
          {saving ? 'Creating…' : 'Create project'}
        </button>
        <button type="button" onClick={onDone} className="glass-btn" style={{ padding: '6px 12px', color: C.textMuted }}>
          Cancel
        </button>
      </div>
    </GlassPanel>
  );
}

// ---------------------------------------------------------------------------
// Project detail
// ---------------------------------------------------------------------------

const TASK_STATUS_ORDER: ProjectTask['status'][] = ['todo', 'in-progress', 'blocked', 'done'];
const TASK_STATUS_LABEL: Record<ProjectTask['status'], string> = {
  todo: 'To do',
  'in-progress': 'In progress',
  blocked: 'Blocked',
  done: 'Done',
};

function ProjectDetail({
  project,
  tasks,
  projects,
  canMutate,
  actor,
}: {
  project: Project;
  tasks: ProjectTask[];
  projects: Project[];
  canMutate: boolean;
  actor: string;
}) {
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [attLabel, setAttLabel] = useState('');
  const [attUrl, setAttUrl] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [addingTask, setAddingTask] = useState(false);

  const addTask = async () => {
    const title = newTaskTitle.trim();
    if (!title || addingTask) return;
    setAddingTask(true);
    try {
      await createTask({
        title,
        projectId: project.id,
        projectName: project.title,
        accountId: project.clientId,
        accountName: project.clientName,
        createdBy: actor,
      });
      setNewTaskTitle('');
    } finally {
      setAddingTask(false);
    }
  };

  // Who can be @mentioned: the project's owner + members + the assignee roster.
  const mentionCandidates = useMemo(() => {
    const set = new Set<string>([...TASK_ASSIGNEES]);
    if (project.ownerName) set.add(project.ownerName);
    for (const m of project.memberNames) set.add(m);
    return [...set];
  }, [project.ownerName, project.memberNames]);

  const projectTasks = useMemo(
    () => tasks.filter((t) => t.projectId === project.id),
    [tasks, project.id],
  );
  const selectedTask = tasks.find((t) => t.id === selectedTaskId) ?? null;
  const { pct, done, total } = completionOf(project.id, tasks);

  const patch = (p: Parameters<typeof updateProject>[1]) => {
    void updateProject(project.id, p, actor);
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || project.tags.includes(t)) {
      setTagInput('');
      return;
    }
    patch({ tags: [...project.tags, t] });
    setTagInput('');
  };

  const removeTag = (t: string) => patch({ tags: project.tags.filter((x) => x !== t) });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header card */}
      <GlassPanel variant="default" animate={false} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{project.title}</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{project.clientName}</div>
          </div>
          {canMutate && (
            <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
              {project.status === 'archived' ? (
                <button type="button" onClick={() => patch({ status: 'active' })} className="glass-btn" style={{ padding: '5px 10px', color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <ArchiveRestore size={13} /> Restore
                </button>
              ) : (
                <button type="button" onClick={() => patch({ status: 'archived' })} className="glass-btn" style={{ padding: '5px 10px', color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Archive size={13} /> Archive
                </button>
              )}
            </div>
          )}
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ fontSize: 11, color: C.textMuted, display: 'flex', flexDirection: 'column', gap: 3 }}>
            Status
            <select style={fieldStyle} disabled={!canMutate} value={project.status} onChange={(e) => patch({ status: e.target.value as ProjectStatus })}>
              {PROJECT_STATUSES.map((s) => (
                <option key={s} value={s} style={{ background: C.ink800 }}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 11, color: C.textMuted, display: 'flex', flexDirection: 'column', gap: 3 }}>
            Priority
            <select style={fieldStyle} disabled={!canMutate} value={project.priority} onChange={(e) => patch({ priority: e.target.value as TaskPriority })}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p} style={{ background: C.ink800 }}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 11, color: C.textMuted, display: 'flex', flexDirection: 'column', gap: 3 }}>
            Due date
            <input type="date" style={fieldStyle} disabled={!canMutate} value={project.dueDate ?? ''} onChange={(e) => patch({ dueDate: e.target.value || null })} />
          </label>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>
              Completion — {pct}% ({done}/{total})
            </div>
            <CompletionBar pct={pct} />
          </div>
        </div>

        {/* Description */}
        <label style={{ fontSize: 11, color: C.textMuted, display: 'flex', flexDirection: 'column', gap: 3 }}>
          Description
          <textarea
            style={{ ...fieldStyle, minHeight: 54, resize: 'vertical' }}
            disabled={!canMutate}
            defaultValue={project.description ?? ''}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== (project.description ?? '')) patch({ description: v || undefined });
            }}
            placeholder="Add a description…"
          />
        </label>

        {/* Owner + members */}
        {(project.ownerName || project.memberNames.length > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.textMuted }}>
            <Users size={13} />
            {project.ownerName && <span><strong style={{ color: C.text }}>{project.ownerName}</strong> (owner)</span>}
            {project.memberNames.filter((m) => m !== project.ownerName).map((m) => (
              <span key={m}>{m}</span>
            ))}
          </div>
        )}

        {/* Tags */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <Tag size={13} style={{ color: C.textDim }} />
          {project.tags.map((t) => (
            <span key={t} style={{ fontSize: 11, color: C.magentaSoft, background: 'rgba(255,0,255,0.10)', padding: '2px 8px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {t}
              {canMutate && (
                <button type="button" onClick={() => removeTag(t)} style={{ background: 'none', border: 'none', color: C.magentaSoft, cursor: 'pointer', padding: 0, display: 'inline-flex' }}>
                  <X size={10} />
                </button>
              )}
            </span>
          ))}
          {canMutate && (
            <input
              style={{ ...fieldStyle, padding: '3px 8px', width: 110 }}
              placeholder="add tag…"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addTag();
                }
              }}
            />
          )}
        </div>

        {/* Links */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link2 size={13} /> Links
          </div>
          {project.links.map((l) => (
            <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <a href={l.url} target="_blank" rel="noreferrer" style={{ color: C.blueLight, fontSize: 13, textDecoration: 'none' }}>
                {l.label}
              </a>
              {canMutate && (
                <button type="button" onClick={() => void removeProjectLink(project.id, l.id)} style={{ background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', display: 'inline-flex' }}>
                  <X size={11} />
                </button>
              )}
            </div>
          ))}
          {canMutate && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <input style={{ ...fieldStyle, padding: '3px 8px', width: 130 }} placeholder="label" value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} />
              <input style={{ ...fieldStyle, padding: '3px 8px', flex: 1, minWidth: 160 }} placeholder="https://…" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
              <button
                type="button"
                disabled={!linkLabel.trim() || !linkUrl.trim()}
                onClick={() => {
                  void addProjectLink(project.id, linkLabel.trim(), linkUrl.trim());
                  setLinkLabel('');
                  setLinkUrl('');
                }}
                className="glass-btn"
                style={{ padding: '3px 10px', color: C.textMuted, opacity: !linkLabel.trim() || !linkUrl.trim() ? 0.5 : 1 }}
              >
                Add
              </button>
            </div>
          )}
        </div>
      </GlassPanel>

      {/* Task list grouped by status */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Tasks ({projectTasks.length})
        </div>
        {canMutate && (
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              style={{ ...fieldStyle, flex: 1 }}
              placeholder="Add a task to this project…"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void addTask();
                }
              }}
            />
            <button
              type="button"
              className="glass-btn"
              onClick={() => void addTask()}
              disabled={!newTaskTitle.trim() || addingTask}
              style={{ padding: '6px 12px', color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', opacity: !newTaskTitle.trim() || addingTask ? 0.5 : 1 }}
            >
              <Plus size={13} /> Add task
            </button>
          </div>
        )}
        {projectTasks.length === 0 ? (
          <span style={{ fontSize: 13, color: C.textDim }}>No tasks yet — add one above. Click any task to open its full detail.</span>
        ) : (
          TASK_STATUS_ORDER.map((status) => {
            const group = projectTasks.filter((t) => t.status === status);
            if (group.length === 0) return null;
            return (
              <div key={status} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 11, color: C.textDim, fontWeight: 600 }}>
                  {TASK_STATUS_LABEL[status]} · {group.length}
                </div>
                {group.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTaskId(t.id)}
                    title="Open task detail"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid transparent', cursor: 'pointer', width: '100%', textAlign: 'left' }}
                  >
                    <span style={{ fontSize: 13, color: t.status === 'done' ? C.textDim : C.text, textDecoration: t.status === 'done' ? 'line-through' : 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.title}
                    </span>
                    <span style={{ fontSize: 11, color: PRIORITY_COLOR[t.priority], fontWeight: 600 }}>{t.priority}</span>
                    {t.assignee && <span style={{ fontSize: 11, color: C.textMuted }}>{t.assignee}</span>}
                    {t.dueDate && <span style={{ fontSize: 11, color: C.textDim }}>{t.dueDate}</span>}
                  </button>
                ))}
              </div>
            );
          })
        )}
      </GlassPanel>

      {selectedTask && (
        <TaskDetail
          task={selectedTask}
          allTasks={tasks}
          projects={projects}
          canMutate={canMutate}
          actor={actor}
          onClose={() => setSelectedTaskId(null)}
          onDeleted={() => setSelectedTaskId(null)}
        />
      )}

      {/* Attachments */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Paperclip size={13} /> Attachments ({project.attachments.length})
        </div>
        {project.attachments.map((a) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <a href={a.url} target="_blank" rel="noreferrer" style={{ color: C.blueLight, fontSize: 13, textDecoration: 'none' }}>
              {a.label}
            </a>
            <span style={{ fontSize: 11, color: C.textDim }}>· added by {a.addedBy}</span>
            {canMutate && (
              <button type="button" onClick={() => void removeProjectAttachment(project.id, a.id)} style={{ background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', display: 'inline-flex' }}>
                <X size={11} />
              </button>
            )}
          </div>
        ))}
        {project.attachments.length === 0 && <span style={{ fontSize: 12, color: C.textDim }}>No attachments yet.</span>}
        {canMutate && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <FileAttachButton scope="project" id={project.id} addedBy={actor} onAttach={(att) => addProjectAttachment(project.id, att)} />
            <span style={{ fontSize: 11, color: C.textDim }}>or link:</span>
            <input style={{ ...fieldStyle, padding: '3px 8px', width: 110 }} placeholder="label" value={attLabel} onChange={(e) => setAttLabel(e.target.value)} />
            <input style={{ ...fieldStyle, padding: '3px 8px', flex: 1, minWidth: 140 }} placeholder="https://…" value={attUrl} onChange={(e) => setAttUrl(e.target.value)} />
            <button
              type="button"
              disabled={!attLabel.trim() || !attUrl.trim()}
              onClick={() => {
                void addProjectAttachment(project.id, { kind: 'link', label: attLabel.trim(), url: attUrl.trim(), addedBy: actor });
                setAttLabel('');
                setAttUrl('');
              }}
              className="glass-btn"
              style={{ padding: '3px 10px', color: C.textMuted, opacity: !attLabel.trim() || !attUrl.trim() ? 0.5 : 1 }}
            >
              Add link
            </button>
          </div>
        )}
      </GlassPanel>

      {/* Comments */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 6 }}>
          <MessageSquare size={13} /> Comments ({project.comments.length})
        </div>
        {project.comments.length === 0 && <span style={{ fontSize: 12, color: C.textDim }}>No comments yet. Use @ to mention a teammate.</span>}
        {project.comments.map((c) => (
          <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '6px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{c.authorName}</span>
              <span style={{ fontSize: 10, color: C.textDim }}>{new Date(c.createdAt).toLocaleString()}</span>
            </div>
            <span style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap' }}>{c.body}</span>
            {c.mentions.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {c.mentions.map((m) => (
                  <span key={m} style={{ fontSize: 10, color: C.magentaSoft, background: 'rgba(255,0,255,0.10)', padding: '1px 6px', borderRadius: 999 }}>
                    @{m}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {canMutate && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <MentionTextarea
              style={{ ...fieldStyle, minHeight: 48, resize: 'vertical', width: '100%' }}
              placeholder="Write a comment… use @Name to mention a teammate"
              value={commentBody}
              onChange={setCommentBody}
              candidates={mentionCandidates}
            />
            {(() => {
              const preview = parseMentions(commentBody, mentionCandidates);
              return preview.length > 0 ? (
                <div style={{ fontSize: 11, color: C.textMuted }}>
                  Mentions: {preview.map((m) => `@${m}`).join(', ')}
                </div>
              ) : null;
            })()}
            <div>
              <button
                type="button"
                disabled={!commentBody.trim()}
                onClick={() => {
                  const body = commentBody.trim();
                  if (!body) return;
                  const mentions = parseMentions(body, mentionCandidates);
                  void addProjectComment(project.id, actor, body, mentions);
                  // In-app bell already surfaces this; also queue mention emails
                  // (Brevo in live mode; queued-only in local mock).
                  void queueMentionEmails({
                    mentions,
                    authorName: actor,
                    context: `the project "${project.title}"`,
                  });
                  setCommentBody('');
                }}
                className="glass-btn"
                style={{ padding: '5px 12px', border: '1px solid rgba(255,0,255,0.40)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: !commentBody.trim() ? 0.5 : 1 }}
              >
                <Send size={12} /> Comment
              </button>
            </div>
          </div>
        )}
      </GlassPanel>
    </div>
  );
}
